/* Raw task telemetry formatter.
   Static/Janison reporting paths must only format telemetry supplied by the
   response contract. Synthetic telemetry is retained only for demo/mock mode
   so production packs never fabricate item counts, durations, or rates from
   percentiles. */

export interface TaskRawDetails {
  itemsAttempted: number;
  itemsCorrect: number;
  durationSec: number;
  durationLabel: string;
  accuracyPct: number; // 0-100
  correctPerMin: number | null;
}

interface TaskItemEvidence {
  questionId?: string;
  question_id?: string;
  correct?: boolean | number | null;
  score?: number | null;
  validScored?: boolean | null;
  valid_scored?: boolean | null;
}

export const DMT10_2026_TARGET_HIDING_ITEM_IDS = new Set([
  "DMT8_NEW-5hiding-copy",
  "DMT8_NEW-7hiding-copy",
  "DMT10_NEW-6hiding-copy",
  "DMT10_NEW-3hiding-copy",
  "DMT5_NEW-4hiding-copy-3",
  "DMT5_NEW-2hiding-copy-3",
]);

function durationParts(durationSec: number): { durationSec: number; durationLabel: string } {
  const duration = Math.max(1, Math.round(durationSec));
  const mins = Math.floor(duration / 60);
  const secs = duration % 60;
  return { durationSec: duration, durationLabel: `${mins}:${secs.toString().padStart(2, '0')}` };
}

export function dmt10TargetItemScore(item: TaskItemEvidence): number | null {
  if (item.validScored === false || item.valid_scored === false) return null;
  if (typeof item.score === 'number' && Number.isFinite(item.score)) return item.score;
  if (typeof item.correct === 'boolean') return item.correct ? 1 : 0;
  if (typeof item.correct === 'number' && Number.isFinite(item.correct)) return item.correct;
  return null;
}

/* Formats supplied item telemetry. If `allowSynthetic` is true, falls back to
   deterministic demo telemetry from percentile + stable seed. */
export function taskRawDetails(
  studentId: string,
  taskId: string,
  percentile: number,
  itemsTotal: number,
  empirical?: { itemsAttempted?: number; scoreSum?: number; durationSec?: number; correctPerMinute?: number | null; itemResponses?: TaskItemEvidence[] },
  options: { allowSynthetic?: boolean } = {},
): TaskRawDetails | null {
  const normalisedTaskId = taskId.toLowerCase();
  const isNumberLine = normalisedTaskId.startsWith('bnl') || normalisedTaskId.startsWith('unl');
  const isDmt10 = normalisedTaskId === 'dmt10' || normalisedTaskId === 'dmt10_2026';
  if (isDmt10 && empirical?.itemResponses?.length) {
    const targetScores = empirical.itemResponses
      .filter((item) => DMT10_2026_TARGET_HIDING_ITEM_IDS.has(String(item.questionId ?? item.question_id ?? '').trim()))
      .map(dmt10TargetItemScore)
      .filter((score): score is number => score != null);
    const itemsAttempted = Math.max(0, Math.min(itemsTotal, targetScores.length));
    const itemsCorrect = Math.max(0, Math.min(itemsAttempted, Math.round(targetScores.reduce((sum, score) => sum + score, 0))));
    const hasDuration = empirical.durationSec != null && empirical.durationSec > 0;
    const duration = hasDuration ? durationParts(empirical.durationSec as number) : { durationSec: 0, durationLabel: "—" };
    const accuracyPct = itemsAttempted > 0 ? (itemsCorrect / itemsAttempted) * 100 : 0;
    return {
      itemsAttempted,
      itemsCorrect,
      durationSec: duration.durationSec,
      durationLabel: duration.durationLabel,
      accuracyPct,
      correctPerMin: hasDuration ? empirical.correctPerMinute ?? itemsCorrect / (duration.durationSec / 60) : null,
    };
  }
  const hasRequiredEmpiricalEvidence =
    empirical?.itemsAttempted != null && empirical.scoreSum != null &&
    (isNumberLine || empirical.durationSec != null);
  if (hasRequiredEmpiricalEvidence) {
    const itemsAttempted = Math.max(0, Math.min(itemsTotal, Math.round(empirical.itemsAttempted as number)));
    const itemsCorrect = isNumberLine
      ? Math.max(0, Math.min(itemsAttempted, Math.round((empirical.scoreSum as number) * 10) / 10))
      : Math.max(0, Math.min(itemsAttempted, Math.round(empirical.scoreSum as number)));
    const hasDuration = empirical.durationSec != null;
    const duration = hasDuration ? durationParts(empirical.durationSec as number) : { durationSec: 0, durationLabel: "—" };
    const accuracyPct = itemsAttempted > 0 ? (itemsCorrect / itemsAttempted) * 100 : 0;
    const correctPerMin = isNumberLine ? null : empirical.correctPerMinute ?? (duration.durationSec > 0 ? itemsCorrect / (duration.durationSec / 60) : 0);
    return { itemsAttempted, itemsCorrect, durationSec: duration.durationSec, durationLabel: duration.durationLabel, accuracyPct, correctPerMin };
  }

  if (!options.allowSynthetic) return null;

  let h = 0;
  for (let i = 0; i < studentId.length + taskId.length; i++) {
    h = ((h << 5) - h) + (studentId + taskId).charCodeAt(i % (studentId.length + taskId.length));
    h |= 0;
  }
  const maxItems = itemsTotal;
  // Items attempted: most students attempt nearly all; lower percentile → slightly fewer.
  // The 25 here is a mock-shaping constant for telemetry plausibility,
  // NOT a band cutoff (bands use the P15/P35 convention).
  const attemptJitter = ((h >>> 8) % 5); // 0..4
  const itemsAttempted = Math.max(
    Math.min(8, maxItems), // floor must not exceed itemsTotal
    Math.min(maxItems, maxItems - attemptJitter - (percentile < 25 ? 2 : 0)),
  );
  // Number Line tasks are continuous: students click along a line and
  // receive a 0–1 score based on 1 - percentage absolute error, not a
  // discrete correct / incorrect count. Keep itemsAttempted discrete, but
  // store the continuous mean score in `itemsCorrect` for the demo data
  // layer; display helpers render it as a score rather than "correct".
  const correctJitter = ((h >>> 0) % 5) - 2; // -2..+2
  const continuousScore = Math.max(0, Math.min(1, Math.round((percentile / 100) * 100) / 100));
  const itemsCorrect = isNumberLine
    ? Math.round(continuousScore * itemsAttempted * 10) / 10
    : Math.max(
        0,
        Math.min(itemsAttempted, Math.round((percentile / 100) * itemsAttempted) + correctJitter),
      );
  // Duration: lower percentile → longer; add noise (seconds).
  const durationJitter = ((h >>> 4) % 60) - 30; // ±30 s
  const durationSec = Math.max(30, Math.round(300 - percentile * 1.8 + durationJitter));
  const mins = Math.floor(durationSec / 60);
  const secs = durationSec % 60;
  const durationLabel = `${mins}:${secs.toString().padStart(2, '0')}`;
  const accuracyPct = isNumberLine
    ? continuousScore * 100
    : itemsAttempted > 0 ? (itemsCorrect / itemsAttempted) * 100 : 0;
  const correctPerMin = isNumberLine ? 0 : durationSec > 0 ? itemsCorrect / (durationSec / 60) : 0;
  return { itemsAttempted, itemsCorrect, durationSec, durationLabel, accuracyPct, correctPerMin };
}
